'use client';

import { createRoot } from 'react-dom/client';
import EventEmitter from 'events';
import { Socket } from 'socket.io-client';
import { EventInput } from '@fullcalendar/core';
import { searchTasks, updateTaskById } from '@nx-frontend/shared/api';
import {
  TasksListType,
  TaskSubjectChangeCalendarViewEvent,
  TaskSubjectDashboardFilterChanges,
  TaskSubjectEvent,
  TaskSubjectEventType,
  ThemeHexColorSchemas,
  WindowNimbusCommon,
} from '@nx-frontend/shared/types';
import { sendOpenTaskEvent } from '@nx-frontend/shared/utils';
import { TasksComponent } from './tasks';
import { TasksBoardComponent } from './tasks-board';
import { TasksSocketEvents } from './tasks-socket-events';
import { TasksOptions, TasksRequestQueryFilters } from './types';
import { getTaskStartAndEndDates, getThemeCssVars, mapToCalendarTasks } from './util';

export class TasksController {
  private roots: Record<string, boolean> = {};
  private inited = false;
  private sessionId: string | null = '';
  private gatewayUrl = '';
  private windowCommon: WindowNimbusCommon | null = null;
  static instance: TasksController;
  public emitter: EventEmitter = new EventEmitter();
  public t: (key: string) => string = () => '';
  private options:
    | {
        staticAssetPrefix: string;
        nimAllowedActions: Record<string, unknown>;
        workspacesIds: string[];
      }
    | undefined = undefined;

  static getInstance() {
    if (!this.instance) {
      this.instance = new TasksController();
      this.instance.emitter.setMaxListeners(1000);
    }
    if (!this.instance.windowCommon) {
      this.instance.initSubscriptions();
    }
    return this.instance;
  }

  public init(
    sessionId: string | null,
    gatewayUrl: string,
    socket: Socket | null,
    t: (key: string) => string,
    options?: {
      staticAssetPrefix: string;
      nimAllowedActions: Record<string, unknown>;
      workspacesIds: string[];
    }
  ) {
    if (this.sessionId !== sessionId || this.gatewayUrl !== gatewayUrl) {
      this.sessionId = sessionId;
      this.gatewayUrl = gatewayUrl;
    }
    this.options = options;
    this.t = t;
    if (socket !== undefined && socket !== null) {
      TasksSocketEvents.getInstance().init({
        listenSocketEvent: (event: string, callback) => {
          socket.on(event, callback);
        },
        onTaskCreateEvent: this.onTaskCreateEvent,
        onTaskUpdateEvent: this.onTaskUpdateEvent,
        onTaskRemoveEvent: this.onTaskRemoveEvent,
        onBoardUpdateEvent: this.onBoardUpdateEvent,
      });
    }
    if (!this.inited) {
      this.inited = true;
    }
  }

  private initSubscriptions() {
    if (typeof window !== 'undefined' && '__NIMBUS_COMMON' in window) {
      this.windowCommon = window.__NIMBUS_COMMON as unknown as WindowNimbusCommon;
    }
    if (this.windowCommon) {
      this.windowCommon.taskEventsSubj$.subscribe((event) => {
        this.handleTaskEvents(event);
      });
    }
  }

  private handleTaskEvents(event: TaskSubjectEvent) {
    if (event.eventType === TaskSubjectEventType.CHANGE_CALENDAR_VIEW) {
      const eventValue = event.value as TaskSubjectChangeCalendarViewEvent;
      const tasksAsEventInput = eventValue.tasks
        ? eventValue.tasks.map((task) => {
            const { start, end } = getTaskStartAndEndDates(task.showTime, task.deadlineDate, false);
            return {
              id: task.uniqueId,
              title: task.title,
              done: task.done,
              className: task.done ? 'completed-task' : '',
              start,
              end,
              extendedProps: {
                originalTime: task.deadlineDate ? task.deadlineDate : null,
                taskListId: task.taskListId,
              },
            };
          })
        : [];
      this.onChangeCalendarViewEvent(
        eventValue.id || 'dashboard',
        eventValue.isCalendarEnabled,
        tasksAsEventInput,
        eventValue.forceTasksUpdate
      );
    } else if (event.eventType === TaskSubjectEventType.DASHBOARD_FILTER_CHANGES) {
      this.onDashboardFiltersChanged(event.value as TaskSubjectDashboardFilterChanges);
    }
  }

  public async getTasks(filters: TasksRequestQueryFilters) {
    if (this.sessionId) {
      const query = {
        filters:
          !filters.workspaceIds.length && this.options?.workspacesIds
            ? Object.assign({}, filters, { workspaceIds: this.options.workspacesIds })
            : filters,
      };
      const response = await searchTasks({ gatewayUrl: this.gatewayUrl, sessionId: this.sessionId, query });
      if (response && response.tasks && response.tasks.length) {
        const tasks = mapToCalendarTasks(response.tasks);
        return tasks;
      }
    } else {
      return null;
    }
    return [];
  }

  public openDetailTask(taskId: string, type: TasksListType) {
    if (type === TasksListType.DASHBOARD) {
      const eventParams = {
        taskId,
        staticAssetPrefix: this.options ? this.options.staticAssetPrefix : '/box/file/',
        nimCommentsDisabled: false,
        nimAttachmentsDisabled: false,
        nimHideComments: false,
        nimHideAttachments: false,
        nimAllowedActions: this.options && this.options.nimAllowedActions,
        nimDetailedViewFloating: true,
        nimDescriptionDisabled: false,
      };
      const openTaskEvent = new CustomEvent('ng2:tasks:open-floating-task', { detail: eventParams });
      document.dispatchEvent(openTaskEvent);
    } else {
      sendOpenTaskEvent(taskId);
    }
  }

  public createTask(taskListId: string, workspaceId: string, deadlineDate?: number | null, showTime = false) {
    const openTaskEvent = new CustomEvent('ng2:tasks:create-task', {
      detail: { taskListId, workspaceId, deadlineDate, showTime },
    });
    document.dispatchEvent(openTaskEvent);
  }

  public completeTask(taskId: string, done: boolean, workspaceId: string) {
    if (this.sessionId === null) return;
    updateTaskById({
      gatewayUrl: this.gatewayUrl,
      taskId,
      workspaceId,
      sessionId: this.sessionId,
      data: {
        task: {
          done,
        },
      },
    });
  }

  public onTaskCreateEvent = (taskListId: string, task: EventInput) => {
    this.emitter.emit('createTask', { taskListId, task });
  };

  public onTaskUpdateEvent = (taskListId: string, task: EventInput) => {
    this.emitter.emit('updateTask', { taskListId, task });
  };

  public onTaskRemoveEvent = (taskListId: string, taskId: string) => {
    this.emitter.emit('removeTask', { taskListId, taskId });
  };

  public onBoardUpdateEvent = (boardId: string, taskListIds?: string[]) => {
    this.emitter.emit('boardUpdate', { boardId, taskListIds });
  };

  private onChangeCalendarViewEvent(
    featureId: string,
    isCalendarEnabled: boolean,
    tasks: EventInput[],
    force: boolean
  ) {
    this.emitter.emit('change-calendar-view', { featureId, isCalendarEnabled, tasks, force });
  }

  public onFeaturePropsChanged(featureId: string, props: Record<string, unknown>) {
    this.emitter.emit('feature-props-changed', { featureId, props });
  }

  private onDashboardFiltersChanged(filters: TaskSubjectDashboardFilterChanges) {
    this.emitter.emit('dashboard-filters-changed', filters);
  }

  private renderCalendarThemeStyles(theme?: ThemeHexColorSchemas) {
    const isRendered = () => {
      return Boolean(document.getElementById('full-calendar-theme-vars'));
    };
    if (theme && !isRendered()) {
      const style = document.createElement('style');
      style.id = 'full-calendar-theme-vars';
      const css = getThemeCssVars(theme);
      style.appendChild(document.createTextNode(css));
      document.head.appendChild(style);
    }
  }

  public render(
    featureId: string,
    featureType: TasksListType = TasksListType.TASK_LIST,
    workspaceId: string,
    node: Element,
    options: TasksOptions = {}
  ) {
    this.renderCalendarThemeStyles(options.themeHexColors);
    let rendered = this.roots[featureId];
    if (rendered) {
      rendered = Boolean(node.childNodes.length);
      if (!rendered) {
        delete this.roots[featureId];
      }
    }
    if (!rendered) {
      node.classList.add('tasks-with-calendar');
      const domRoot = createRoot(node);
      const ToRenderComponent = featureType === 'task-list' ? TasksComponent : TasksBoardComponent;
      domRoot.render(
        <ToRenderComponent
          featureId={featureId}
          workspaceId={workspaceId}
          options={options}
          type={featureType}
          t={this.t}
          controller={this}
        />
      );
    }
  }
}
